import { gql } from '@apollo/client';

import type { StateEnumType } from '@jerry-serverless/enums/state';
import type { Income } from '@jerry-serverless/types';
import type { AddressDescription } from '@jerry-serverless/enums/address_description';
import type { FaultAccidentType, ViolationsCountType } from '@jerry-serverless/enums/user';

import type { NotInsuredReason } from '../../types/not_insured_reason.enum';
import type { Education } from '../../types/education.enum';
import type { ReferralSource } from '../../types/referral_source.enum';
import type { AddressInput } from '../../types/address';
import type { AutoInsuredLength } from '../../types/auto_insured_length.enum';
import type { CarUsageEditKey, OwnershipEnum } from '../../types/car';
import type { HomeOwnership } from '../../types/homeownership.enum';
import type { InitialCar } from '../../types/car';
import type { ShopInsuranceTypes } from '../../types/shop_for_insurance';
import type { ShopFrequency } from '../../types/shop_frequency.enum';
import type { NameVariation } from '../../types/name';
import type { DeviceInfoType, SelectedServiceType } from '../../types/app_data';
import type { CreditRange } from '../../types/credit_range.enum';

import type { User } from './user.fragment';
import { USER_FRAGMENT } from './user.fragment';

/**
 * Mutation to update user attributes
 */
export const UPDATE_USER_MUTATION = gql`
  mutation UpdateUser($input: UpdateUser5Input!) {
    updateUser5(input: $input) {
      user {
        ${USER_FRAGMENT}
      }
    }
  }
`;

export enum SignupStep {
  verify = 'verify',
  middlename = 'middlename',
  shopfor = 'shopfor',
  dob = 'dob',
  nomatch = 'nomatch',
  address = 'address',
  describeaddress = 'describeaddress',
  cars = 'cars',
  loading = 'loading',
  claims = 'claims',
  faultAccident = 'faultAccident',
  violations = 'violations',
  violationsCount = 'violationsCount',
  occupation = 'occupation',
  addInitialCar = 'addInitialCar',
  education = 'education',
  coverage = 'coverage',
  discounts = 'discounts',
  mileage = 'mileage',
  premium = 'premium',
  insured = 'insured',
  notinsured = 'notinsured',
  homeownership = 'homeownership',
  financedCars = 'financedCars',
  ownership = 'ownership',
  referral = 'referral',
  creditrange = 'creditrange',
  driverlicense = 'driverlicense',
  income = 'income',
  loanBalance = 'loanbalance',
  loanPayment = 'loanpayment',
  minNumberPayments = 'minnumberpayments',
  dwep = 'dwep',
  garage = 'garage',
  finish = 'finish',
  estimatedMileage = 'estimatedmileage',
  email = 'email',
  householdVehicle = 'householdVehicle',
  nnoConfirm = 'nnoConfirm',
}

export interface UpdateUser5PremiumInput {
  term?: number;
  premium?: number;
}

export interface UpdateUser5UserDiscount {
  isHomeowner?: boolean;
  telematics?: boolean;
}

export interface UpdateUser5DriverDiscount {
  driverId: string;
  education?: Education;
  goodStudent?: boolean;
  occupation?: string | null;
  industry?: string | null;
  driverTraining?: boolean;
}

export interface UpdateUser5CarDiscount {
  carId: string;
  usage?: CarUsageEditKey | null;
  ridesharing?: boolean;
  annualMileage?: number | null;
}

export interface UpdateUser5DiscountInput {
  userDiscounts?: UpdateUser5UserDiscount;
  driversDiscounts?: UpdateUser5DriverDiscount[];
  carsDiscounts?: UpdateUser5CarDiscount[];
}

export interface UpdateUser5CarInput {
  id?: string;
  year?: string;
  make?: string;
  model?: string;
  submodel?: string;
  vin?: string;
  isPartialVin?: string;
  annualMileage?: number;
  ownership?: OwnershipEnum;
}

export enum WelcomePage {
  dob = 'dob',
  nomatch = 'nomatch',
  address = 'address',
}

export interface UpdateUserInput {
  input: {
    name?: string;
    step?: SignupStep;
    signature?: string;
    dateOfBirth?: string;
    address?: AddressInput;
    claims?: boolean;
    faultAccident?: FaultAccidentType | null;
    violations?: boolean;
    violationsCount?: ViolationsCountType | null;
    violationsNotSure?: boolean;
    carPremium?: UpdateUser5PremiumInput;
    homePremium?: UpdateUser5PremiumInput;
    discounts?: UpdateUser5DiscountInput;
    cars?: UpdateUser5CarInput[];
    isAutoInsured?: boolean;
    addressBusiness?: boolean;
    addressDescription?: AddressDescription;
    autoInsuredLength?: AutoInsuredLength;
    continuousCoverageLength?: number;
    isAutoCarrierChanged?: boolean;
    welcomePage?: WelcomePage;
    notInsuredReason?: NotInsuredReason;
    furthestStep?: SignupStep;
    isLoadingDone?: boolean;
    dobChanged?: boolean;
    addressChanged?: boolean;
    referralSource?: ReferralSource;
    referralOther?: string;
    appOnboard?: boolean;
    ptoken?: string;
    isHomeowner?: boolean;
    homeOwnership?: HomeOwnership;
    occupation?: string | null;
    industry?: string | null;
    updateOccupDriverId?: string;
    updateEduDriverId?: string;
    education?: Education;
    hasCarLoan?: boolean;
    initialCar?: InitialCar;
    shopFor?: ShopInsuranceTypes;
    shopFrequency?: ShopFrequency;
    shopForEnabled?: boolean;
    reorderSignUpEnabled?: boolean;
    deviceInfo?: DeviceInfoType;
    gClientID?: string;
    askVehicleOwnership?: boolean;
    askReferral?: boolean;
    appSignup?: boolean;
    nameVariation?: NameVariation;
    disableRefi?: boolean; // Flag to skip generateInitialOffersMobile or not
    creditRange?: CreditRange;
    dlState?: StateEnumType;
    dlNumber?: string;
    income?: Income;
    carLoanBalance?: number;
    carLoanMonthlyPayment?: number;
    hasMinNumberCarLoanPayments?: boolean | null;
    isDwepServiceSelected?: boolean;
    isGarageServiceSelected?: boolean;
    isLastOnboardingStep?: boolean;
    selectedServices?: Array<keyof SelectedServiceType>;
    estimatedMileage?: number;
    multipleCarsAndDrivers?: boolean;
    email?: string;
    validated?: boolean;
    needNNOInsurance?: boolean;
    hasHouseholdVehicle?: boolean;
    bodilyInjuryTier?: number;
    carsWithFullCoverage?: string[];
    askCoverageV4Variant?: number;
  };
}

export interface UpdateUserData {
  errors?: { message: string }[];
  updateUser5: {
    user: User;
  };
}
